import React from "react";
import { BsChevronLeft } from "react-icons/bs";
import { useAuthProvider } from "../../context/Auth";
import { getNotifications } from "../../services/notification.service";
import { getCookie } from "../../services/cookie.service";
import { INotification } from "../../interfaces/notification";

const notificationTypeMap: Record<string, string> = {
  INFO: "Informação",
  WARNING: "Aviso",
  ERROR: "Erro",
  SUCCESS: "Sucesso",
  REQUEST: "Solicitação",
  REQUESTED_PRODUCT: "Solicitação de Produto",
  APPROVED: "Aprovado",
  REJECTED: "Rejeitado",
  PENDING: "Pendente",
  CREATED: "Criado",
  UPDATED: "Atualizado",
  DELETED: "Removido",
};

const notificationTextMap: Record<string, string> = {
  notification: "notificação",
  notifications: "notificações",
  request: "solicitação",
  requests: "solicitações",
  donation: "doação",
  donations: "doações",
  distribution: "distribuição",
  distributions: "distribuições",
  shelter: "abrigo",
  shelters: "abrigos",
  "distribution point": "ponto de distribuição",
  "distribution points": "pontos de distribuição",
  "demand point": "ponto de demanda",
  "demand points": "pontos de demanda",
  coordinator: "coordenador",
  coordinators: "coordenadores",
  created: "criado",
  updated: "atualizado",
  deleted: "removido",
  approved: "aprovado",
  rejected: "rejeitado",
  pending: "pendente",
};

const textReplacementOrder = Object.keys(notificationTextMap).sort(
  (a, b) => b.length - a.length,
);

function applyTextReplacement(
  input: string,
  source: string,
  target: string,
) {
  return input.split(source).join(target);
}

function translateNotificationText(text: string) {
  let translated = text;

  textReplacementOrder.forEach((source) => {
    const target = notificationTextMap[source];
    translated = applyTextReplacement(translated, source, target);
    translated = applyTextReplacement(
      translated,
      source.charAt(0).toUpperCase() + source.slice(1),
      target.charAt(0).toUpperCase() + target.slice(1),
    );
    translated = applyTextReplacement(
      translated,
      source.toUpperCase(),
      target.charAt(0).toUpperCase() + target.slice(1),
    );
  });

  return translated;
}

function translateNotificationType(type: string) {
  return notificationTypeMap[type] ?? translateNotificationText(type);
}


interface INotificationProps {
  open: boolean;
  close: () => void;
}

export function Notification({ open, close }: INotificationProps) {
  const [notifications, setNotifications] = React.useState<INotification[]>([])
  const token = getCookie("token");
  const { currentUser } = useAuthProvider();

  React.useEffect(() => {
    (async () => {
      if (token) {
        const resp = await getNotifications();
        setNotifications(resp.data);
      }
    })();
  }, [token]);

  if (!currentUser) return null;

  return (
    <aside
      className={`
        fixed z-10 inset-0 top-0 left-0 w-screen h-screen
        flex justify-end transition-all
        ${open ? "visible bg-gray-100/30 backdrop-blur-sm" : "invisible"}
      `}
      onClick={close}
    >
      <div
        className={`
          h-full overflow-hidden flex flex-col
          w-full sm:w-[375px] p-4 transition ease 
          duration-[0.4s] bg-white relative
          ${open ? "translate-x-0" : "translate-x-full"}
        `}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="relative flex justify-center items-center min-h-20">
          <BsChevronLeft
            className="absolute left-0 top-1/2 -translate-y-1/2 text-lg cursor-pointer"
            onClick={close}
          />
          <h2 className="text-lg font-semibold">Notificações</h2>
        </div>

        <div className="overflow-y-auto my-2 mb-14 not-scroll-bar">
          <ul className="flex flex-col gap-2">
            <ul className="flex flex-col gap-2">
              {notifications.length === 0 ? (
  <li className="text-center text-gray-500 text-sm py-4">
    Sem notificações
  </li>
              ) : (
                notifications.map((n) => (
                  <li
                    key={n.id}
                    className="p-3 rounded-md border border-gray-200 shadow-sm flex flex-col"
                  >
                    <span className="text-sm font-semibold">{translateNotificationType(n.type)}</span>
                    <span className="text-gray-700 text-sm">{translateNotificationText(n.message)}</span>
                    <span className="text-xs text-gray-500 mt-1">
                      {new Date(n.createdAt).toLocaleString("pt-BR")}
                    </span>
                  </li>
                ))
              )}

            </ul>
          </ul>
        </div>
      </div>
    </aside>
  );
}
